import React, { useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTenancy } from '@/contexts/TenancyContext';
import { MeetingAgendaModal } from '@/components/meeting-agenda/meeting-agenda-modal';
import { CalendarDays } from 'lucide-react';
import { RoleSelector } from './RoleSelector';
import { useMSGClauses } from '@/app/pages/msg/contexts/ClausesContext';

type UpdatePageClausesFn = (newClauses: Set<string>) => void;

interface MDXFieldsProps {
  moduleList: { id: string; visible: boolean }[];
  clauseStandards: React.MutableRefObject<Set<string>>;
  pageClauses: { current: Set<string> };
  updatePageClauses: UpdatePageClausesFn;
  isReadOnly?: boolean;
}

// Backward-compatible signature: supports both object props and legacy positional args
export function useMarkdownComponents(
  propsOrModuleList: any,
  legacyClauseStandards?: any,
  legacyPageClauses?: any,
  legacyUpdatePageClauses?: any,
  legacyIsReadOnly?: boolean
) {
  const { activeTenancy } = useTenancy();
  const msgClausesCtx = useMSGClauses?.();

  // Unify parameters
  let moduleList: any[] = [];
  let clauseStandards: React.MutableRefObject<Set<string>> = { current: new Set<string>() } as any;
  let pageClauses: React.MutableRefObject<Set<string>> | undefined = undefined;
  let updatePageClauses: ((s: Set<string>) => void) | undefined = undefined;
  let isReadOnly = false;

  if (propsOrModuleList && typeof propsOrModuleList === 'object' && 'moduleList' in propsOrModuleList) {
    const p = propsOrModuleList as MDXFieldsProps;
    moduleList = p.moduleList || [];
    clauseStandards = (p.clauseStandards as any) || { current: new Set<string>() };
    pageClauses = p.pageClauses;
    updatePageClauses = p.updatePageClauses;
    isReadOnly = !!p.isReadOnly;
  } else {
    // Legacy positional usage
    moduleList = propsOrModuleList || [];
    clauseStandards = (legacyClauseStandards as any) || { current: new Set<string>() };
    pageClauses = legacyPageClauses;
    updatePageClauses = legacyUpdatePageClauses;
    isReadOnly = !!legacyIsReadOnly;
  }

  const isVisible = (standard: string) => {
    if (!moduleList || !standard) return false;
    const module = moduleList.find((m) => m && m.id === standard);
    // Fall back to enabled if visible not set
    return module ? (module.visible ?? module.enabled ?? false) : false;
  };

  const isEnabled = (standard: string) => {
    if (!moduleList || !standard) return false;
    const module = moduleList.find((m) => m && m.id === standard);
    return module ? (module.enabled ?? module.visible ?? false) : false;
  };

  return {
    // Standard HTML Tags for applying classes
    ul: ({ children }) => (
      <ul className="list-disc pl-5 space-y-1">{children}</ul>
    ),
    li: ({ children }) => (
      <li>{children}</li>
    ),


    // Custom components
    CompanyName: () => <>{activeTenancy.name || ''}</>,
    CompanyLegalName: () => <>{activeTenancy.legalName || ''}</>,
    StandardSpecific: ({ standard, children }) => {
      if (!isEnabled(standard)) return null;
      return <>{children}</>;
    },
    StandardListItem: ({ standard, children }) => {
      if (!isEnabled(standard)) return null;
      return <li>{children}</li>;
    },
    ClauseStatement: ({ standard, clauseRef, children }: { standard: string; clauseRef?: string; children: any }) => {
      if (!standard || standard === '' || standard === null || standard === undefined) return null;
      // Extract clause number from the string (stable across renders for same content)
      const clauseNumber = clauseRef || (children?.toString?.().split(": ")[0]?.split(" ")[1] || '');

      // Side-effects: register clause once when discovered and update outer page state
      React.useEffect(() => {
        if (!isEnabled(standard)) return;
        if (clauseNumber) {
          // Update per-page clause set only if changed
          if (pageClauses) {
            const current = pageClauses.current || new Set<string>();
            if (!current.has(clauseNumber)) {
              const next = new Set(current);
              next.add(clauseNumber);
              updatePageClauses && updatePageClauses(next);
            }
          }
          // Register in shared MSG clauses context
          if (msgClausesCtx) {
            msgClausesCtx.registerClause(standard, clauseNumber);
          }
        }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [standard, clauseNumber]);

      if (!isVisible(standard)) return null;
      const variantName = standard.toLowerCase();
      return <Badge variant={variantName as any}>{children}</Badge>;
    },
    MeetingAgenda: () => {
      const [isModalOpen, setIsModalOpen] = useState(false);

      return (
        <>
          <Button
            onClick={() => setIsModalOpen(true)}
            className={`mt-4 gap-2 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isReadOnly}
          >
            <CalendarDays className="h-4 w-4" />
            Generate Meeting Agenda
          </Button>
          {!isReadOnly && (
            <MeetingAgendaModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
          )}
        </>
      );
    },
    RoleSelector: ({ standard, settingsKey, value, onChange, className = "", readOnly = false }: { standard: string; settingsKey: string; value?: string; onChange?: (value: string) => void; className?: string; readOnly?: boolean }) => (
      <RoleSelector
        standard={standard as 'iso27701' | 'iso42001'}
        settingsKey={settingsKey}
        value={value}
        onChange={onChange}
        className={className}
        readOnly={readOnly}
      />
    ),
    PIIControllerRole: () => (
      <RoleSelector
        standard="iso27701"
        settingsKey="piiControllerRole"
        readOnly={isReadOnly}
      />
    ),
    AIRole: () => (
      <RoleSelector
        standard="iso42001"
        settingsKey="aiRole"
        readOnly={isReadOnly}
      />
    ),
  };
}
